import { generatePath } from 'react-router-dom';
import { ApiCacheTags, baseApiService } from '@qapline/api';
import { DEFAULT_CONFIG } from '@qapline/config';
import { z } from 'zod';

import {
    carrierExtendedSchema,
    carrierListItemSchema,
    carrierSchema,
    customerCarrierDetailSchema,
    customerCarrierListItemSchema,
    TCarrier,
    TCarrierExtended,
    TCarrierListItem,
    TCustomerCarrierDetail,
    TCustomerCarrierListItem,
} from '../schemas';
import { TListCarriersRequestQuery } from '../types';

const carrierEndpoints = DEFAULT_CONFIG.api.endpoints.carrier;
const carrierTags = ApiCacheTags.carrier;

export const carrierApi = baseApiService.injectEndpoints({
    endpoints: (builder) => ({
        listCarriers: builder.query<{ data: TCarrier[]; count: number }, TListCarriersRequestQuery>({
            query: ({ filters, take, skip, order, search }) => ({
                url: carrierEndpoints.list,
                method: 'GET',
                params: {
                    take,
                    skip,
                    search,
                    order: order ? JSON.stringify(order) : undefined,
                    filters: filters ? JSON.stringify(filters) : undefined,
                },
            }),
            providesTags: (res, error) => (error ? [] : [{ type: carrierTags.list }]),
            transformResponse: (data) =>
                z
                    .object({
                        data: carrierSchema.array(),
                        count: z.number(),
                    })
                    .parse(data),
        }),
        listCustomerCarriers: builder.query<
            { data: TCustomerCarrierListItem[]; count: number },
            TListCarriersRequestQuery
        >({
            query: ({ filters, take, skip, order, search }) => ({
                url: carrierEndpoints.list,
                method: 'GET',
                params: {
                    take,
                    skip,
                    search,
                    order: order ? JSON.stringify(order) : undefined,
                    filters: filters ? JSON.stringify(filters) : undefined,
                },
            }),
            providesTags: (res, error) => (error ? [] : [{ type: carrierTags.list }]),
            transformResponse: (data) =>
                z
                    .object({
                        data: customerCarrierListItemSchema.array(),
                        count: z.number(),
                    })
                    .parse(data),
        }),
        getCarrierList: builder.query<TCarrierListItem[], void>({
            query: () => ({
                url: carrierEndpoints.getList,
                method: 'GET',
            }),
            providesTags: (_res, error) => (error ? [] : [{ type: carrierTags.list }]),
            transformResponse: (data) => carrierListItemSchema.array().parse(data),
        }),
        getCarrier: builder.query<TCarrierExtended, number>({
            query: (carrierId) => ({
                url: generatePath(carrierEndpoints.get, { carrierId: carrierId.toString() }),
                method: 'GET',
            }),
            providesTags: (res, error, carrierId) => (error ? [] : [{ type: carrierTags.item, id: carrierId }]),
            transformResponse: (data) => carrierExtendedSchema.parse(data),
        }),
        getCustomerCarrier: builder.query<TCustomerCarrierDetail, number>({
            query: (carrierId) => ({
                url: generatePath(carrierEndpoints.get, { carrierId: carrierId.toString() }),
                method: 'GET',
            }),
            providesTags: (res, error, carrierId) => (error ? [] : [{ type: carrierTags.item, id: carrierId }]),
            transformResponse: (data) => customerCarrierDetailSchema.parse(data),
        }),
        addCustomerCarrier: builder.mutation<void, number>({
            query: (carrierId) => ({
                url: generatePath(carrierEndpoints.customerRelation, { carrierId: carrierId.toString() }),
                method: 'PUT',
            }),
            invalidatesTags: (_res, error, carrierId) =>
                error ? [] : [{ type: carrierTags.list }, { type: carrierTags.item, id: carrierId }],
        }),
        removeCustomerCarrier: builder.mutation<void, number>({
            query: (carrierId) => ({
                url: generatePath(carrierEndpoints.customerRelation, { carrierId: carrierId.toString() }),
                method: 'DELETE',
            }),
            invalidatesTags: (_res, error, carrierId) =>
                error ? [] : [{ type: carrierTags.list }, { type: carrierTags.item, id: carrierId }],
        }),
    }),
});

export const {
    useAddCustomerCarrierMutation,
    useGetCarrierQuery,
    useGetCustomerCarrierQuery,
    useGetCarrierListQuery,
    useLazyGetCarrierListQuery,
    useLazyGetCarrierQuery,
    useLazyListCarriersQuery,
    useListCarriersQuery,
    useListCustomerCarriersQuery,
    useRemoveCustomerCarrierMutation,
} = carrierApi;
